/* eslint-disable no-sparse-arrays */
import React from 'react';
import { Link } from 'react-router-dom';
import accounting from 'accounting';
import moment from 'moment';
import _ from 'underscore';
import { unescapeHTML, titleize } from 'underscore.string';
import geolib from 'geolib';
import { Translation } from 'react-i18next';
import { LoadDimensionFormatter, LoadUnitFormatter, } from '../master/formatters';
import { DEFAULT_DISTANCE_UNIT } from 'utils/localization/types';
import formatMoney from 'utils/formatters/Money';
const distanceFormatter = (cell) => (React.createElement("span", null,
    cell ? geolib.convertUnit(DEFAULT_DISTANCE_UNIT, cell) : '-',
    ' ',
    DEFAULT_DISTANCE_UNIT));
const addressFormatter = (cell) => {
    const label = cell.street || cell.name;
    return (React.createElement("div", null,
        cell.name && (React.createElement("p", { className: "mb-1 font-bold text-grey-dark" }, unescapeHTML(cell.name))),
        React.createElement("p", { className: "mb-2" },
            label,
            " ",
            cell.suite_or_apt && React.createElement("span", null,
                "Suite ",
                cell.suite_or_apt)),
        React.createElement("p", null,
            cell.city,
            ", ",
            cell.state,
            " ",
            cell.zipcode)));
};
const actionsFormatter = (cell, row, rowIndex, extra) => {
    const expired = row.expired || moment().isAfter(row.expiration_date);
    const link = `/booking/${row.id}`;
    return (React.createElement(Translation, null, (t, { i18n }) => (React.createElement("div", { className: "flex-col flex-wrap justify-around" },
        React.createElement(Link, { to: link },
            React.createElement("button", { href: link, style: { height: '35px' }, className: "flex items-center text-white button button-green", disabled: expired }, t('BOOK_SHIPMENT'))),
        React.createElement("div", { className: "flex flex-wrap mt-2" },
            extra && extra.onEdit && (React.createElement("button", { className: "flex items-center mr-5 text-white button button-blue w-35", onClick: () => extra.onEdit(row.id), disabled: expired, style: { marginRight: '8px' } }, t('EDIT'))),
            extra && extra.onDelete && (React.createElement("button", { className: "block w-10 h-10 text-red-700 rounded-full outline-none bg-red-50 hover:bg-red-100", onClick: () => extra.onDelete(row.id) },
                React.createElement("div", { className: "flex items-center justify-center" },
                    React.createElement("i", { className: "mr-0 fa fa-trash-o" })))))))));
};
const detailLinkFormatter = (cell, row) => {
    const link = `/quotes/${row._id}`;
    return React.createElement(Link, { to: link }, cell);
};
const moneyFormatter = (cell, row) => {
    if (!cell) {
        return React.createElement("span", null, "none");
    }
    return React.createElement("span", null, formatMoney(cell, row === null || row === void 0 ? void 0 : row.currency));
};
const wrappedFormatter = (cell) => {
    if (!cell) {
        return React.createElement("span", null, "none");
    }
    return React.createElement("span", { style: { wordWrap: 'break-word' } }, cell);
};
const dateFormat = 'LL';
const dateTime = (cell, _row, _rowIndex, extra) => {
    if (!cell)
        return React.createElement("span", null, "None");
    return moment(cell).format(dateFormat);
};
const expirationDate = (cell, _row, _rowIndex, extra) => {
    if (!cell)
        return React.createElement("span", null, "None");
    let date = moment(cell).format(dateFormat);
    const expired = moment().isAfter(cell);
    const color = expired ? { color: 'red' } : { color: 'black' };
    return React.createElement("span", { style: color }, date);
};
export const loadDetailsFormatter = (cell, row) => {
    const rows = [];
    if (row.full_truckload) {
        rows.push(React.createElement("div", { key: "full-truckload-cell" }, "Full Truckload"));
    }
    if (!row.full_truckload && (!row.loads || !row.loads.length)) {
        return React.createElement("span", null, "None");
    }
    _.each(row.loads, (l, i) => {
        rows.push(React.createElement("div", { key: i },
            React.createElement(LoadDimensionFormatter, { quantity: l.quantity, type: l.type, length: l.length, width: l.width, height: l.height, dimensional_units: l.dimensional_units })));
    });
    rows.push(React.createElement("div", { key: "load-weight", className: "mt-2" },
        React.createElement(LoadUnitFormatter, { weight: accounting.format(row.weight), weight_units: row.weight_units })));
    return React.createElement("div", null, rows);
};
const columns = {
    admin: [
        {
            dataField: 'quote_id',
            text: 'Quote ID',
            localizationSlug: 'QUOTE_ID',
            headerStyle: {
                width: '5%',
            },
            formatter: detailLinkFormatter,
        },
        {
            dataField: 'created_at',
            text: 'Created date/time',
            localizationSlug: 'CREATED_DATE_TIME',
            headerStyle: {
                width: '5%',
            },
            formatter: dateTime,
        },
        {
            dataField: 'created_by',
            text: 'Created By',
            localizationSlug: 'CREATED_BY',
            headerStyle: {
                width: '5%',
            },
            formatter: wrappedFormatter,
        },
        // {
        //   dataField: 'shipper.company_name',
        //   text: 'Shipper',
        //   headerStyle: {
        //     width: '10%',
        //   },
        // },
        {
            dataField: 'shipment_type',
            text: 'Type',
            localizationSlug: 'TYPE',
            headerStyle: {
                width: '5%',
            },
        },
        {
            dataField: 'origin[0]',
            text: 'Origin Address',
            localizationSlug: 'ORIGIN_ADDRESS',
            headerStyle: {
                width: '10%',
            },
            formatter: addressFormatter,
        },
        {
            dataField: 'destination[0]',
            text: 'Destination Address',
            localizationSlug: 'DESTINATION_ADDRESS',
            headerStyle: {
                width: '10%',
            },
            formatter: addressFormatter,
        },
        {
            dataField: 'distance',
            text: 'Distance',
            localizationSlug: 'DISTANCE',
            titleize: true,
            headerStyle: {
                width: '4%',
            },
            formatter: distanceFormatter,
        },
        // ex. (Pallets - 3)
        {
            dataField: 'loads',
            text: 'Load',
            localizationSlug: 'LOAD',
            headerStyle: {
                width: '5%',
            },
            formatter: loadDetailsFormatter,
        },
        // Accessorials Dollar Amount
        {
            dataField: 'shipper_accessorial_costs',
            text: 'Accessorials',
            localizationSlug: 'ACCESSORIAL',
            count: 2,
            headerStyle: {
                width: '5%',
            },
            formatter: moneyFormatter,
        },
        // When does the quote expire
        {
            dataField: 'total_cost',
            text: 'Quote Amount',
            localizationSlug: 'QUOTE_AMOUNT',
            headerStyle: {
                width: '5%',
            },
            formatter: moneyFormatter,
        },
        {
            dataField: 'expiration_date',
            text: 'Valid Until',
            localizationSlug: 'VALID_UNTIL',
            headerStyle: {
                width: '5%',
            },
            formatter: expirationDate,
        },
        {
            dataField: 'shipper.id',
            text: 'Actions',
            localizationSlug: 'ACTIONS',
            headerStyle: {
                width: '10%',
            },
            formatter: actionsFormatter,
            formatExtraData: {},
        },
    ],
    shipper: [
        {
            dataField: 'quote_id',
            text: 'QUOTE_ID',
            headerStyle: {
                width: '5%',
            },
            formatter: detailLinkFormatter,
        },
        {
            dataField: 'created_at',
            text: 'CREATED_DATE_TIME',
            headerStyle: {
                width: '5%',
            },
            formatter: dateTime,
        },
        // {
        //   dataField: 'shipper.company_name',
        //   text: 'Shipper',
        //   headerStyle: {
        //     width: '10%',
        //   },
        // },
        {
            dataField: 'shipment_type',
            text: 'TYPE',
            headerStyle: {
                width: '5%',
            },
        },
        {
            dataField: 'origin[0].address',
            text: 'ORIGIN_ADDRESS',
            headerStyle: {
                width: '10%',
            },
        },
        {
            dataField: 'destination[0].address',
            text: 'DESTINATION_ADDRESS',
            headerStyle: {
                width: '10%',
            },
        },
        {
            dataField: 'distance',
            text: 'DISTANCE',
            headerStyle: {
                width: '4%',
            },
            formatter: distanceFormatter,
        },
        // ex. (Pallets - 3)
        {
            dataField: 'loads',
            text: 'LOAD',
            headerStyle: {
                width: '5%',
            },
            formatter: loadDetailsFormatter,
        },
        // Accessorials Dollar Amount
        {
            dataField: 'shipper_accessorial_costs',
            text: 'ACCESSORIAL',
            count: 2,
            headerStyle: {
                width: '5%',
            },
            formatter: moneyFormatter,
        },
        // When does the quote expire
        {
            dataField: 'total_cost',
            text: 'QUOTE_AMOUNT',
            headerStyle: {
                width: '5%',
            },
            formatter: moneyFormatter,
        },
        {
            dataField: 'expiration_date',
            text: 'VALID_UNTIL',
            headerStyle: {
                width: '5%',
            },
            formatter: expirationDate,
        },
        {
            dataField: '_id',
            text: 'ACTIONS',
            headerStyle: {
                width: '10%',
            },
            formatter: actionsFormatter,
            formatExtraData: {},
        },
    ],
};
export default {
    columns,
};
